import angular from 'angular';
import _ from 'lodash-es';
import State from '~/scripts/services/model/state';
import Action from '~/scripts/services/model/action';

/* globals Knitiv */

export default class WfNodeEditorController {
	/**
	 * @type {{
	 *   isa:  import('@knitiv/api-client-javascript').KNode,
	 *   kid:  import('@knitiv/api-client-javascript').KNode,
	 *   name: string
	 * }}
	 */
	#roleTous;

	/* @ngInject */
	/**
	 * @param {Knitiv.RootScope} $rootScope
	 * @param {any} $scope
	 * @param {import('angular').ITimeoutService} $timeout
	 * @param {import('angular').translate.ITranslateService} $translate
	 * @param {any} NodeService
	 * @param {any} Notification
	 * @param {any} User
	 */
	constructor(
		$rootScope,
		$scope,
		$timeout,
		$translate,
		NodeService,
		Notification,
		User,
	) {
		this.#roleTous = {
			isa: 'K_NODE;KROLE',
			kid: 'K_NODE;KR_WF_TOUS',
			name: $translate.instant('common.all'),
		};
		this.$rootScope = $rootScope;
		this.$scope = $scope;
		this.$timeout = $timeout;
		this.$translate = $translate;
		this.NodeService = NodeService;
		this.Notification = Notification;
		this.User = User;

		this.context = this.$rootScope.context;

		/** @type {boolean} */
		this.create = false;

		/** @type {boolean} */
		this.disabled = true;

		/** @type {?import('../wf-editor/wf-editor.controller').default} */
		this.editor = null;

		/** @type {?(import('angular').IFormController)} */
		this.formNode = null;

		/** @type {any} */
		this.jsonEditorOptions = {
			mode:    this.disabled ? 'view' : 'tree',
			modes:   this.disabled ? [] : [
				'code',
				'form',
				'text',
				'tree',
				'view',
			], // allowed modes
			onError: (/** @type {any} */ error) => {
				this.Notification.warning({
					title:   this.$translate.instant('error.title'),
					message: error.toString(),
				});
			},
		};

		/** @type {any} */
		this.mf = {
			card:              '0-N',
			allocators:        [],
			delegationGranted: [],
			custom:            {
				selectable: [
					'K_NODE;WF_ROLE',
					'K_NODE;KPROFIL',
				],
			},
			validate:          true,
		};

		/** @type {?(State | Action)} */
		this.node = null;

		/** @type {any} */
		this.operations = undefined;

		/**
		 * @type {?{
		 *  create: boolean,
		 *  modified: boolean,
		 *  node: import('~/scripts/services/model/action').default | import('~/scripts/services/model/state').default,
		 *  operations: any,
		 *  tab: 'info' | 'droit' | 'allocator' | 'delegation'
		 * }}
		 */
		this.selected = null;

		/** @type {State[]} */
		this.states = [];

		/** @type {any} */
		this.tmp = {
			commentRequired: false,
			enableTargetStateAllocators: false,
		};
	}

	cancel() {
		this.selected = null;
		if (this.editor?.tab) {
			if (this.editor.tab.selected) {
				this.editor.tab.selected.node.selected = false;
				this.editor.tab.selected = null;
			}
			if (this.node && this.create) {
				if (this.node instanceof State) {
					this.editor.tab.stateDelete(this.node);
				}
				else if (this.node instanceof Action) {
					this.editor.tab.actionDelete(this.node);
				}
			}
		}
		this.node = null;
	}

	/**
	 * @param {string} name
	 */
	async createRole(name) {
		if (name) {
			const role = this.NodeService.create('K_NODE;WF_ROLE', `K_NODE;${this.User.getRef()}`);
			role.setName(name);

			await this.NodeService.update(role);
			this.Notification.success({
				title:   this.$translate.instant('object.successfully-created-title'),
				message: this.$translate.instant('object.successfully-created-message'),
			});
			/** @deprecated */
			this.$scope.$emit('admin-wf-load-roles');

			this.mf.allocators.at(-1).kid = role.kid;
			this.editor?.tab?.schema.addRole({
				kid:  role.kid,
				name: role.name,
				isa:  role.isa.objid,
			});
		}
	}

	delete() {
		if (this.node) {
			this.editor?.tab?.nodeDelete(this.node);
		}
	}

	/**
	 * @returns {boolean}
	 */
	isAdmin() {
		return !!this.$rootScope?.context?.profil.admin
			|| this.$rootScope.context?.role.sql_in.includes('K_NODE;KR_ADMIN_WF')
			|| false;
	}

	/**
	 * @param {string} op
	 * @param {any} flow
	 * @param {any} item
	 */
	setOp(op, flow, item) {
		if (item && this.selected) {
			if (
				item.kid in this.selected.operations
				&& (
					(this.selected.operations[item.kid].op === 'DEL' && op === 'ADD')
					|| (this.selected.operations[item.kid].op === 'ADD' && op === 'DEL')
				)
			) {
				delete this.selected.operations[item.kid];
			}
			else {
				this.selected.operations[item.kid] = {
					op,
					flow,
					item,
				};
			}
		}
	}

	/**
	 */
	submit() {
		if (this.node) {
			this.node.allocators.length = 0;
			for (const allocator of this.mf.allocators) {
				try {
					this.node.addAllocator(allocator.kid);
					if (!(this.editor?.tab?.schema.kidlist.some((/** @type {any} */ r) => r.kid === allocator.kid))) {
						this.editor?.tab?.schema.addRole({
							kid:  allocator.kid,
							name: allocator.name,
							isa:  allocator.isa,
						});
					}
				}
				catch (error) {
					console.error(error);
				}
			}
		}

		if (this.node instanceof State) {
			if (!Array.isArray(this.node.delegationGranted)) {
				this.node.delegationGranted = [];
			}

			this.node.delegationGranted.length = 0;
			for (const delegationGranted of this.mf.delegationGranted) {
				try {
					this.node.addDelegationGranted(delegationGranted.kid);
					if (!(this.editor?.tab?.schema.kidlist.some((/** @type {any} */ dg) => dg.kid === delegationGranted.kid))) {
						this.editor?.tab?.schema.addDelegationGranted({
							kid:  delegationGranted.kid,
							name: delegationGranted.name,
							isa:  delegationGranted.isa,
						});
					}
				}
				catch (error) {
					console.error(error);
				}
			}
		}
		else if (this.node instanceof Action) {
			this.node.kjson = angular.toJson({
				commentRequired: this.tmp.commentRequired,
				enableTargetStateAllocators: this.tmp.enableTargetStateAllocators,
			});
		}

		if (this.node && this.editor?.tab) {
			if (this.create) {
				this.editor.tab.nodeAdd(this.node, this.operations);
			}
			else {
				this.editor.tab.nodeUpdate(this.node, this.operations);
			}
		}
	}

	/**
	 * @param {import('angular').IOnChangesObject} changes
	 */
	$onChanges(changes) {
		if (changes.selected) {
			this.node = this.selected ? _.cloneDeep(this.selected.node) : null;
			this.operations = this.selected?.operations;
			this.create = this.selected?.create || false;
			this.tab = this.selected?.tab;

			this.$timeout(() => {
				if (this.editor?.tab) {
					if (this.node?.allocators) {
						this.mf.allocators.length = 0;
						this.mf.delegationGranted.length = 0;

						if (this.node.allocators.length === 0) {
							this.node.allocators.push(this.#roleTous.kid);
							if (!this.editor.tab.schema.kidlist.some(
								(/** @type {any} */ item) => item.kid === this.#roleTous.kid,
							)) {
								this.editor.tab.schema.kidlist.push(this.#roleTous);
							}
						}

						for (const allocator of this.node.allocators) {
							for (const item of this.editor.tab.schema.kidlist) {
								if (allocator === item.kid) {
									// @ts-ignore
									this.mf.allocators.push(item);
								}
							}
						}
					}

					if (this.node instanceof State) {
						if (this.node.delegationGranted) {
							for (const delegationGranted of this.node.delegationGranted) {
								for (const item of this.editor.tab.schema.kidlist) {
									if (delegationGranted === item.kid) {
										// @ts-ignore
										this.mf.delegationGranted.push(item);
									}
								}
							}
						}

						if (this.node.droit?.length > 0) {
							for (const droit of this.node.droit) {
								if (!droit.role.isa) {
									const d = this.editor.tab.schema.kidlist
										.find((/** @type {any} */ item) => item.kid === droit.role.kid);
									if (d) {
										droit.role.isa = d.isa;
									}
								}
							}
						}
					}

					this.$scope.$apply();
				}
			});

			if (this.create) {
				if (this.node instanceof State && this.node.isa.objid === 'K_NODE;WF_STATE' && this.editor?.tab?.schema) {
					this.node.initial = this.editor.tab.schema.states.length === 0 ? 1 : 0;
				}

				this.node?.addDroit({
					kright:    8,
					role:  {
						kid:  'K_NODE;KR_WF_TOUS',
						isa:  'K_NODE;KROLE',
						name: this.$translate.instant('common.all'),
					},
				});
			}

			if (this.node instanceof Action && this.editor?.tab?.schema) {
				for (const state of this.editor.tab.schema.states) {
					// @ts-ignore
					this.states.push({
						kid:  state.kid,
						name: state.getName(),
					});
				}
				for (const flow of [
					'from',
					'to',
				]) {
					this.tmp[flow] = [];
					// @ts-ignore
					for (const kid of Object.keys(this.node[`state${flow}`])) {
						// @ts-ignore
						const state = this.editor.tab.schema.states[this.editor.tab.schema.getKeyStateFromKid(kid)];
						if (state) {
							this.tmp[flow].push({
								kid,
								name: state.getName(),
							});
						}
					}
				}
				if (this.node.kjson) {
					let kjson;
					try {
						kjson = JSON.parse(this.node.kjson);
					}
					catch {
						// eslint-disable-next-line prefer-destructuring
						kjson = this.node.kjson;
					}
					this.tmp.commentRequired = kjson?.commentRequired ?? false;
					this.tmp.enableTargetStateAllocators = kjson?.enableTargetStateAllocators ?? false;
				}
			}
		}
	}

	$onInit() {
		// if we select "K_NODE;KR_TOUS" in the MF, then we have to delete all other selected items
		// "K_NODE;KR_TOUS" should be the only one selected
		this.$scope.$watch('$ctrl.mf.allocators', (/** @type {any} */ allocators) => {
			if (allocators?.some((/** @type {any} */ allocator) => allocator.kid === this.#roleTous.kid)) {
				_.remove(allocators, allocator => allocator.kid !== this.#roleTous.kid);
			}
		}, true);

		this.$scope.$watch('$ctrl.formNode.$dirty', () => {
			if (this.selected && this.formNode) {
				this.selected.modified = this.formNode.$dirty;
			}
		});

		this.$scope.$on('admin-wf-submit-node', () => this.submit());
	}
}
